import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import { api } from '@/lib/api';
import { ChevronLeft, ShieldCheck, Star, Users, Globe, Instagram, Twitter, Trophy } from 'lucide-react';
import BadgeCabinet from '@/components/BadgeCabinet';

export default function MarketplaceCoach() {
  const { coachId } = useParams();
  const [data, setData] = useState(null);
  const [achievements, setAchievements] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get(`/marketplace/coaches/${coachId}`),
      api.get(`/achievements/coach/${coachId}`).catch(() => ({ data: { unlocked: [] } })),
    ]).then(([m, a]) => {
      setData(m.data);
      setAchievements(a.data);
    }).finally(() => setLoading(false));
  }, [coachId]);

  if (loading) return <Layout><div className="text-gray-400">Cargando...</div></Layout>;
  if (!data) return <Layout><div className="text-gray-400">Coach no encontrado.</div></Layout>;

  const { coach, listings } = data;

  return (
    <Layout>
      <Link to="/marketplace" className="inline-flex items-center gap-1 text-gold hover:underline mb-4 text-sm uppercase tracking-wider font-h">
        <ChevronLeft className="w-4 h-4" /> Marketplace
      </Link>

      <div className="bg-card-steel border-2 border-gold rounded-lg p-6 mb-8">
        <div className="flex items-start gap-6 flex-wrap">
          {coach.photo_url ? (
            <img src={coach.photo_url} className="w-32 h-32 rounded-lg border-2 border-gold object-cover" />
          ) : (
            <div className="w-32 h-32 rounded-lg border-2 border-gold bg-gold/10 flex items-center justify-center text-gold font-h text-5xl">
              {coach.display_name?.[0]}
            </div>
          )}
          <div className="flex-1 min-w-[250px]">
            <div className="flex items-center gap-2 mb-1">
              <h1 className="font-h uppercase text-4xl">{coach.display_name}</h1>
              <ShieldCheck className="w-7 h-7 text-gold" />
            </div>
            <div className="text-gold uppercase tracking-widest text-xs mb-2">{coach.credentials}</div>
            <div className="text-sm text-gray-300 mb-3">{coach.country} · {coach.team}</div>
            <p className="text-gray-200">{coach.bio}</p>

            <div className="flex items-center gap-5 mt-4">
              <div className="flex items-center gap-1 text-gold">
                <Star className="w-4 h-4 fill-gold" /> <span className="font-mono">{coach.rating?.toFixed(1)}</span>
              </div>
              <div className="flex items-center gap-1 text-gray-300">
                <Users className="w-4 h-4" /> <span className="font-mono">{coach.students}</span> estudiantes
              </div>
              <div className="flex items-center gap-1 text-gray-300">
                <Trophy className="w-4 h-4 text-gold" /> <span className="font-mono">{listings.length}</span> publicaciones
              </div>
            </div>

            {(coach.social_x || coach.social_instagram || coach.social_website) && (
              <div className="flex gap-3 mt-3 text-gray-400 text-sm">
                {coach.social_x && <a className="hover:text-gold inline-flex gap-1"><Twitter className="w-4 h-4" /> {coach.social_x}</a>}
                {coach.social_instagram && <a className="hover:text-gold inline-flex gap-1"><Instagram className="w-4 h-4" /> {coach.social_instagram}</a>}
                {coach.social_website && <a className="hover:text-gold inline-flex gap-1"><Globe className="w-4 h-4" /> {coach.social_website}</a>}
              </div>
            )}
          </div>
        </div>
      </div>

      {achievements && achievements.unlocked && achievements.unlocked.length > 0 && (
        <div className="bg-card-steel border border-gold/30 rounded-lg p-5 mb-6">
          <h2 className="font-h uppercase text-lg text-gold mb-4 flex items-center gap-2">
            <Trophy className="w-5 h-5" /> Logros · {achievements.unlocked.length}/{achievements.total_available}
          </h2>
          <BadgeCabinet unlocked={achievements.unlocked} catalog={achievements.unlocked.map(u => u.badge)} showLocked={false} size="md" />
        </div>
      )}

      <h2 className="font-h uppercase text-2xl mb-4">Jugadas a la venta ({listings.length})</h2>      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {listings.map(p => (
          <Link key={p.id} to={`/marketplace/play/${p.id}`}
            className="bg-card-steel border-2 border-gold/40 rounded-lg p-5 hover-lift block">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="text-xs font-mono uppercase tracking-widest text-gold">{p.category} · {p.subcategory}</div>
              <div className="font-h text-2xl text-gold">${p.price?.toFixed(2)}</div>
            </div>
            <div className="font-h uppercase text-xl text-white mb-2">{p.name}</div>
            <p className="text-sm text-gray-300 line-clamp-2 mb-3">{p.listing_blurb}</p>
            <div className="flex items-center justify-between text-xs">
              <span className="text-gold font-mono border border-gold/30 rounded px-2 py-0.5">{Math.round((p.success_rate || 0.7) * 100)}%</span>
              <span className="text-gray-400">{p.purchases_count || 0} ventas</span>
            </div>
          </Link>
        ))}
      </div>
    </Layout>
  );
}
